import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowLeft, CalendarCheck } from "lucide-react";
import { Seo } from "../components/shared/Seo";
import { SeoHeader } from "../components/shared/SeoHeader";
import { SeoFooter } from "../components/shared/SeoFooter";

const ThankYouPage = () => {
    const fired = useRef(false);

    useEffect(() => {
        if (fired.current) return;
        fired.current = true;
        try {
            if (window.plausible) window.plausible("Booking Confirmed");
        } catch (_) {}
        try {
            if (window.oaiq)
                window.oaiq("measure", "appointment_scheduled", {
                    type: "customer_action",
                });
        } catch (_) {}
    }, []);

    // Charge le script iClosed pour initialiser le widget de confirmation
    useEffect(() => {
        const s = document.createElement("script");
        s.type = "text/javascript";
        s.src = "https://app.iclosed.io/assets/widget.js";
        s.async = true;
        document.body.appendChild(s);
        return () => {
            document.body.removeChild(s);
        };
    }, []);

    return (
        <div className="App bg-[#070707] min-h-screen" data-testid="thankyou-page">
            <Seo
                title="Merci — Votre rendez-vous est confirmé | Brand Your Expertise"
                description="Votre rendez-vous est confirmé. À très vite pour construire votre personal branding."
                canonical="/merci"
                noindex
            />
            <SeoHeader ctaSource="merci" />

            <main className="relative min-h-[80vh] flex items-center overflow-hidden">
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 50% 20%, rgba(195,157,99,0.14), transparent 60%)",
                    }}
                />
                <div className="absolute inset-0 bye-grain pointer-events-none" />

                <div className="relative max-w-3xl mx-auto px-6 md:px-12 text-center py-28 md:py-36">
                    <div className="bye-fadeup is-visible inline-flex items-center justify-center w-20 h-20 rounded-full border border-[#c39d63]/40 bg-[#c39d63]/10 text-[#c39d63] mb-8">
                        <Check className="w-9 h-9" strokeWidth={2} />
                    </div>

                    <div className="inline-flex items-center gap-2 text-xs tracking-[0.28em] uppercase text-[#c39d63] mb-5">
                        <CalendarCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Rendez-vous confirmé
                    </div>

                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[1.02]">
                        Merci,{" "}
                        <span className="italic bye-gold-text">à très vite.</span>
                    </h1>

                    <p className="mt-7 text-base md:text-lg text-white/65 leading-relaxed max-w-xl mx-auto">
                        Votre rendez-vous est confirmé. Vous recevrez les détails
                        par e-mail. D'ici là, préparez vos sujets : on cadrera
                        ensemble votre stratégie de personal branding et vos
                        premières vidéos courtes.
                    </p>

                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <Link
                            to="/"
                            className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/15 text-sm text-white/80 hover:text-white hover:border-[#c39d63]/50 transition-colors"
                            data-testid="thankyou-home-link"
                        >
                            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                            Retour à l'accueil
                        </Link>
                        <Link
                            to="/#portfolio"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#c39d63] text-[#0a0a0a] text-sm font-medium hover:bg-[#e6c891] transition-colors"
                            data-testid="thankyou-portfolio-link"
                        >
                            Voir le portfolio
                        </Link>
                    </div>

                    {/* iClosed — confirmation de réservation */}
                    <div className="mt-14 max-w-2xl mx-auto text-left">
                        <div className="text-xs tracking-[0.2em] uppercase text-[#c39d63] mb-4 text-center">
                            Détails de votre rendez-vous
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-[#0a0a0a]/60 p-3 md:p-4">
                            <div
                                className="call-details-widget"
                                data-url="https://app.iclosed.io/embed"
                                style={{ width: "100%", height: "340px" }}
                                data-testid="iclosed-call-details"
                            />
                        </div>
                    </div>
                </div>
            </main>

            <SeoFooter />
        </div>
    );
};

export default ThankYouPage;
